import type {
    AnatomyBinding,
    AnatomyPart,
    AnatomyState,
    AnatomyVariant,
    ComponentAnatomySpec,
} from "./componentAnatomy";
import { anatomyTextStyle } from "./componentAnatomyText";

// Mined from designSystem/OTPField/OTPField.module.scss. States are slot-local:
// hover and focus paint one slot, invalid and disabled paint every slot.

const parts: readonly AnatomyPart[] = [
    {
        id: "surface",
        label: "Root",
        element:
            "inline-flex .root with no chrome of its own, spacing the slots",
    },
    {
        id: "background",
        label: "Slot",
        element: "40×40 .slot input, background-color via --otp-field-bg",
    },
    {
        id: "border",
        label: "Border",
        element: "1px border on each .slot, via --otp-field-border",
    },
    {
        id: "label",
        label: "Digit",
        element: "centred .slot text, via --otp-field-fg",
    },
    {
        id: "focusRing",
        label: "Focus ring",
        element: "box-shadow on the focused .slot",
    },
];

const states: readonly AnatomyState[] = [
    { id: "resting", label: "Default", selector: "" },
    {
        id: "hover",
        label: "Hover",
        selector: ':hover:not(:read-only, :focus, [data-forced-state="focus"])',
    },
    { id: "focus", label: "Focus", selector: ":focus:not(:read-only)" },
    {
        id: "invalid",
        label: "Invalid",
        selector:
            ':is([aria-invalid="true"], [data-invalid]):not(:disabled, :focus, [data-forced-state="focus"])',
    },
    { id: "disabled", label: "Disabled", selector: ":disabled" },
];

const sharedBindings: readonly AnatomyBinding[] = [
    { partId: "surface", property: "gap", token: "--space-8" },
    {
        partId: "background",
        property: "width",
        literal: "40px",
        note: "A dimension rather than a spacing step, so the sheet declares it outright.",
    },
    { partId: "background", property: "height", literal: "40px" },
    {
        partId: "background",
        property: "padding",
        literal: "0",
        note: "The fixed box and text-align centre the digit.",
    },
    { partId: "border", property: "border-width", token: "--border-width" },
    { partId: "border", property: "border-radius", token: "--radius-m" },
    ...anatomyTextStyle({
        scale: "s",
        weight: "400",
        mixin: "text-s-14-regular",
    }),
    { partId: "label", property: "text-align", literal: "center" },
];

const border = (token: `--${string}`, note?: string): AnatomyBinding => ({
    partId: "border",
    property: "border-color",
    token,
    via: "--otp-field-border",
    note,
});
const digit = (token: `--${string}`): AnatomyBinding => ({
    partId: "label",
    property: "color",
    token,
    via: "--otp-field-fg",
});

export const otpFieldVariant: AnatomyVariant = {
    id: "default",
    label: "Default",
    selector: "",
    restingBindings: [
        {
            partId: "background",
            property: "background-color",
            token: "--bg-primary",
            via: "--otp-field-bg",
        },
        digit("--fg-primary"),
        border("--border-default"),
    ],
    stateBindings: {
        hover: [border("--border-hover")],
        focus: [
            border(
                "--border-accent-focus",
                "A read-only slot keeps the ring without the accent border.",
            ),
            {
                partId: "focusRing",
                property: "box-shadow (spread)",
                token: "--border-width-thick",
            },
            {
                partId: "focusRing",
                property: "box-shadow (color)",
                token: "--focus-ring-color",
            },
        ],
        invalid: [
            digit("--fg-error"),
            border(
                "--border-error",
                "Hover while invalid: --border-error-hover.",
            ),
        ],
        disabled: [digit("--fg-disabled"), border("--border-disabled")],
    },
};

export const otpFieldAnatomy: ComponentAnatomySpec = {
    label: "OTP field",
    schematic: "otpField",
    rootSelector: ".slot",
    variantAxisLabel: "Variant",
    parts,
    states,
    sharedBindings,
    variants: [otpFieldVariant],
};
